import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  listAdvisories,
  createAdvisory,
  updateAdvisory,
  toggleAdvisory,
  deleteAdvisory,
  normalizeAdvisor,
} from '../../services/advisoryService';
import {
  uploadMediaToR2,
  validateAdvisoryImageFile,
  ADVISORY_IMAGE_ACCEPT,
} from '../../services/mediaUploadService';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: space-between;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex: 1;
  min-width: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 160px;
  flex: 1;
  max-width: 280px;
`;

const ToolbarLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.65);
`;

const SearchInput = styled.input`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const FilterSelect = styled.select`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 0.9rem;
  min-width: 140px;

  option {
    color: #1a202c;
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const PageTitle = styled.h2`
  color: #fff;
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.02em;
`;

const Button = styled.button`
  padding: 10px 20px;
  background: rgba(102, 126, 234, 0.95);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #5a67d8;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: #feb2b2;
  border: 1px solid rgba(254, 178, 178, 0.5);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(229, 62, 62, 0.25);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Table = styled.table`
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  background: rgba(0, 0, 0, 0.15);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.55);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.2);
`;

const Td = styled.td`
  padding: 12px 14px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:hover td {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(102, 126, 234, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.$deleted
      ? 'rgba(113, 128, 150, 0.35)'
      : p.$active
        ? 'rgba(72, 187, 120, 0.25)'
        : 'rgba(237, 137, 54, 0.25)'};
  color: ${(p) =>
    p.$deleted ? '#e2e8f0' : p.$active ? '#9ae6b4' : '#fbd38d'};
  border: 1px solid
    ${(p) =>
      p.$deleted
        ? 'rgba(160, 174, 192, 0.4)'
        : p.$active
          ? 'rgba(72, 187, 120, 0.45)'
          : 'rgba(237, 137, 54, 0.45)'};
`;

const ActionsCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 500;

  ${({ $success }) =>
    $success &&
    `
    background: rgba(72, 187, 120, 0.2);
    color: #c6f6d5;
    border: 1px solid rgba(72, 187, 120, 0.35);
  `}
  ${({ $error }) =>
    $error &&
    `
    background: rgba(245, 101, 101, 0.15);
    color: #fed7d7;
    border: 1px solid rgba(245, 101, 101, 0.35);
  `}
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: #f7fafc;
  border-radius: 12px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.75rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1.25rem;
  font-size: 1.2rem;
  color: #1a202c;
  font-weight: 600;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const FullSpan = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const FieldError = styled.span`
  font-size: 0.75rem;
  color: #c53030;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  background: #fff;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background: #edf2f7;
  }
`;

const PrimaryModalButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  background: #5a67d8;
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #4c51bf;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PhotoSection = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: flex-start;
  padding: 1rem;
  background: #edf2f7;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const PhotoPreviewWrap = styled.div`
  width: 112px;
  height: 112px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #cbd5e0;
  border: 2px solid #cbd5e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #4a5568;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PhotoControls = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FileInput = styled.input`
  font-size: 0.8rem;
  color: #2d3748;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #718096;
  line-height: 1.4;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem 1rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.9rem;
`;

const DEBOUNCE_MS = 320;
const BIO_MIN_LEN = 20;

function isValidOptionalUrl(value) {
  const v = String(value || '').trim();
  if (!v) return true;
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function validateForm(values, { allowPendingProfileUpload = false } = {}) {
  const errors = {};
  const name = String(values.name || '').trim();
  if (name.length < 2) errors.name = 'Name is required (min 2 characters).';

  const designation = String(values.designation || '').trim();
  if (!designation) errors.designation = 'Designation is required.';

  const company = String(values.company || '').trim();
  if (!company) errors.company = 'Company is required.';

  const qualification = String(values.qualification || '').trim();
  if (!qualification) errors.qualification = 'Qualification is required.';

  const bio = String(values.bio || '').trim();
  if (bio.length < BIO_MIN_LEN) {
    errors.bio = `Bio is required (at least ${BIO_MIN_LEN} characters).`;
  }

  const profile = String(values.profile_image_url || '').trim();
  if (!profile && !allowPendingProfileUpload) {
    errors.profile_image =
      'Choose a JPEG or PNG profile photo (max 5 MB), then save.';
  } else if (profile && !isValidOptionalUrl(profile)) {
    errors.profile_image = 'Profile image must be a valid http(s) URL.';
  }

  const linkedin = String(values.linkedin_url || '').trim();
  if (linkedin && !isValidOptionalUrl(linkedin)) {
    errors.linkedin_url = 'Enter a valid http(s) URL or leave blank.';
  }

  const rawOrder = values.display_order;
  if (rawOrder === '' || rawOrder === null || rawOrder === undefined) {
    errors.display_order = 'Display order is required.';
  } else {
    const order = Number(rawOrder);
    if (!Number.isFinite(order) || !Number.isInteger(order) || order < 0) {
      errors.display_order = 'Display order must be an integer ≥ 0.';
    }
  }

  return errors;
}

function buildPayload(values) {
  return {
    name: String(values.name || '').trim(),
    designation: String(values.designation || '').trim(),
    company: String(values.company || '').trim(),
    qualification: String(values.qualification || '').trim(),
    bio: String(values.bio || '').trim(),
    profile_image_url: String(values.profile_image_url || '').trim(),
    display_order: Math.max(0, parseInt(values.display_order, 10) || 0),
    linkedin_url: String(values.linkedin_url || '').trim() || undefined,
    category_id: values.category_id || undefined,
  };
}

function buildEditSnapshot(row) {
  return {
    name: String(row.name ?? '').trim(),
    designation: String(row.designation ?? '').trim(),
    company: String(row.company ?? '').trim(),
    qualification: String(row.qualification ?? '').trim(),
    bio: String(row.bio ?? '').trim(),
    profile_image_url: String(row.profile_image_url ?? '').trim(),
    display_order: Math.max(0, parseInt(row.display_order, 10) || 0),
    linkedin_url: String(row.linkedin_url ?? '').trim(),
    category_id: row.category_id || '',
  };
}

/** PATCH body: only fields that differ from the snapshot taken when the edit modal opened. */
function buildPartialUpdate(current, baseline) {
  if (!baseline) return buildPayload(current);
  const patch = {};
  const s = (v) => String(v ?? '').trim();

  if (s(current.name) !== s(baseline.name)) patch.name = s(current.name);
  if (s(current.designation) !== s(baseline.designation)) {
    patch.designation = s(current.designation);
  }
  if (s(current.company) !== s(baseline.company)) patch.company = s(current.company);
  if (s(current.qualification) !== s(baseline.qualification)) {
    patch.qualification = s(current.qualification);
  }
  if (s(current.bio) !== s(baseline.bio)) patch.bio = s(current.bio);

  const curP = s(current.profile_image_url);
  const baseP = s(baseline.profile_image_url);
  if (curP !== baseP) patch.profile_image_url = curP;

  const cOrder = Math.max(0, parseInt(current.display_order, 10) || 0);
  const bOrder = Math.max(0, parseInt(baseline.display_order, 10) || 0);
  if (cOrder !== bOrder) patch.display_order = cOrder;

  const curL = s(current.linkedin_url);
  const baseL = s(baseline.linkedin_url);
  if (curL !== baseL) patch.linkedin_url = curL;

  if (current.category_id !== baseline.category_id) {
    patch.category_id = current.category_id || undefined;
  }

  return patch;
}

function initials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/);
  if (!parts[0]) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function AdvisorAvatar({ name, url }) {
  const [broken, setBroken] = useState(!url);
  useEffect(() => {
    setBroken(!url);
  }, [url]);
  return (
    <Avatar title={name}>
      {!broken && url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
        />
      ) : (
        initials(name)
      )}
    </Avatar>
  );
}

const defaultForm = () => ({
  name: '',
  designation: '',
  company: '',
  qualification: '',
  bio: '',
  profile_image_url: '',
  display_order: 0,
  linkedin_url: '',
  category_id: '',
});

function AdvisoryManagement({ showMessage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [formModal, setFormModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formValues, setFormValues] = useState(defaultForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);
  const editBaselineRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState(null);

  const clearPhotoDraft = useCallback(() => {
    setImageFile(null);
    setPreviewObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory-categories`, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        const data = json?.data || json || [];
        setCategories(Array.isArray(data) ? data.filter(c => c.is_active && !c.deleted_at) : []);
      } else if (res.status === 401 && showMessage) {
        showMessage('Session expired or unauthorized. Please sign in again.', 'error');
      }
    } catch (e) {
      console.error('Error loading categories:', e);
    } finally {
      setCategoriesLoading(false);
    }
  }, [showMessage]);

  const load = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const data = await listAdvisories();
      setRows(data);
    } catch (e) {
      setListError(e.message || 'Could not load advisors.');
      setRows([]);
      if (e.status === 401 && showMessage) {
        showMessage('Session expired or unauthorized. Please sign in again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    load();
    loadCategories();
  }, [load, loadCategories]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim().toLowerCase());
    }, DEBOUNCE_MS);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const filteredRows = useMemo(() => {
    let list = rows;

    if (statusFilter === 'active') {
      list = list.filter((r) => r.is_active && !r.deleted_at);
    } else if (statusFilter === 'inactive') {
      list = list.filter((r) => !r.is_active && !r.deleted_at);
    }

    if (categoryFilter !== 'all') {
      list = list.filter((r) => r.category_id === categoryFilter);
    }

    if (!debouncedSearch) return list;

    return list.filter((r) => {
      const hay = [r.name, r.designation, r.company]
        .join(' ')
        .toLowerCase();
      return hay.includes(debouncedSearch);
    });
  }, [rows, statusFilter, debouncedSearch, categoryFilter]);

  const openCreate = () => {
    editBaselineRef.current = null;
    clearPhotoDraft();
    setFormValues(defaultForm());
    setFieldErrors({});
    setFormModal({ mode: 'create' });
  };

  const openEdit = (row) => {
    editBaselineRef.current = buildEditSnapshot(row);
    clearPhotoDraft();
    setFormValues({
      name: row.name,
      designation: row.designation,
      company: row.company,
      qualification: row.qualification,
      bio: row.bio,
      profile_image_url: row.profile_image_url,
      display_order: row.display_order,
      linkedin_url: row.linkedin_url,
      category_id: row.category_id || '',
    });
    setFieldErrors({});
    setFormModal({ mode: 'edit', id: row.id });
  };

  const closeForm = () => {
    editBaselineRef.current = null;
    clearPhotoDraft();
    setFormModal(null);
    setFieldErrors({});
    setFormValues(defaultForm());
  };

  const onProfileFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.profile_image;
      return next;
    });
    if (!file) return;
    const err = validateAdvisoryImageFile(file);
    if (err) {
      setFieldErrors((prev) => ({ ...prev, profile_image: err }));
      return;
    }
    setImageFile(file);
    setPreviewObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const clearImageSelection = () => {
    clearPhotoDraft();
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.profile_image;
      return next;
    });
  };

  const setBannerMsg = (text, type = 'success') => {
    setBanner({ text, type });
    setTimeout(() => setBanner({ text: '', type: '' }), 4000);
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const initialErrors = validateForm(formValues, {
      allowPendingProfileUpload: Boolean(imageFile),
    });
    if (Object.keys(initialErrors).length) {
      setFieldErrors(initialErrors);
      return;
    }

    setSubmitting(true);
    try {
      let profileUrl = String(formValues.profile_image_url || '').trim();
      if (imageFile) {
        try {
          profileUrl = await uploadMediaToR2(imageFile);
        } catch (upErr) {
          setFieldErrors({
            profile_image: upErr.message || 'Upload failed.',
          });
          return;
        }
        setPreviewObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setImageFile(null);
      }

      const mergedValues = { ...formValues, profile_image_url: profileUrl };
      const postUploadErrors = validateForm(mergedValues);
      if (Object.keys(postUploadErrors).length) {
        setFieldErrors(postUploadErrors);
        return;
      }

      if (formModal.mode === 'edit') {
        const baseline = editBaselineRef.current;
        const patch = buildPartialUpdate(mergedValues, baseline);
        if (Object.keys(patch).length === 0) {
          setFieldErrors({ _form: 'No changes to save.' });
          return;
        }
        await updateAdvisory(formModal.id, patch);
        setBannerMsg('Advisor updated.', 'success');
        if (showMessage) showMessage('Advisor updated successfully.', 'success');
      } else {
        const payload = buildPayload(mergedValues);
        if (payload.linkedin_url === undefined) delete payload.linkedin_url;
        await createAdvisory(payload);
        setBannerMsg('Advisor created.', 'success');
        if (showMessage) showMessage('Advisor created successfully.', 'success');
      }
      closeForm();
      await load();
    } catch (err) {
      const msg = err.message || 'Request failed.';
      setFieldErrors({ _form: msg });
      if (showMessage) showMessage(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const onToggle = async (row) => {
    const prev = rows.slice();
    const nextActive = !row.is_active;
    setRows((p) =>
      p.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r))
    );
    try {
      const raw = await toggleAdvisory(row.id, nextActive);
      const updated = normalizeAdvisor(raw);
      if (updated) {
        setRows((p) => p.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      } else {
        await load();
      }
      if (showMessage) showMessage('Status updated.', 'success');
    } catch (err) {
      setRows(prev);
      const msg = err.message || 'Toggle failed.';
      setBannerMsg(msg, 'error');
      if (showMessage) showMessage(msg, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await deleteAdvisory(deleteTarget.id);
      setDeleteTarget(null);
      if (showMessage) showMessage('Advisor removed.', 'success');
      await load();
    } catch (err) {
      const msg = err.message || 'Delete failed.';
      if (showMessage) showMessage(msg, 'error');
      setBannerMsg(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formMode = formModal?.mode;

  return (
    <Container>
      <TitleRow>
        <PageTitle>Advisory management</PageTitle>
        <Button type="button" onClick={openCreate}>
          Add advisory
        </Button>
      </TitleRow>

      {banner.text && (
        <Message $success={banner.type === 'success'} $error={banner.type === 'error'}>
          {banner.text}
        </Message>
      )}

      <Toolbar>
        <ToolbarLeft>
          <Field>
            <ToolbarLabel htmlFor="adv-search">Search</ToolbarLabel>
            <SearchInput
              id="adv-search"
              placeholder="Name, designation, company"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field>
            <ToolbarLabel htmlFor="adv-filter">Status</ToolbarLabel>
            <FilterSelect
              id="adv-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </FilterSelect>
          </Field>
          <Field>
            <ToolbarLabel htmlFor="adv-cat-filter">Category</ToolbarLabel>
            <FilterSelect
              id="adv-cat-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              disabled={categoriesLoading}
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </FilterSelect>
          </Field>
        </ToolbarLeft>
      </Toolbar>

      {listError && (
        <Message $error>
          {listError}
        </Message>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Image</Th>
              <Th>Name</Th>
              <Th>Designation</Th>
              <Th>Company</Th>
              <Th>Category</Th>
              <Th>Qualification</Th>
              <Th>Order</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <Tr>
                <Td colSpan={9}>
                  <EmptyState>Loading…</EmptyState>
                </Td>
              </Tr>
            ) : filteredRows.length === 0 ? (
              <Tr>
                <Td colSpan={9}>
                  <EmptyState>No advisors match the current filters.</EmptyState>
                </Td>
              </Tr>
            ) : (
              filteredRows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <AdvisorAvatar name={row.name} url={row.profile_image_url} />
                  </Td>
                  <Td>{row.name}</Td>
                  <Td>{row.designation}</Td>
                  <Td>{row.company}</Td>
                  <Td>{row.category_name || '-'}</Td>
                  <Td>{row.qualification}</Td>
                  <Td>{row.display_order}</Td>
                  <Td>
                    <StatusPill $active={row.is_active} $deleted={!!row.deleted_at}>
                      {row.deleted_at ? 'Deleted' : row.is_active ? 'Active' : 'Inactive'}
                    </StatusPill>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <SecondaryButton
                        type="button"
                        onClick={() => openEdit(row)}
                        disabled={!!row.deleted_at}
                      >
                        Edit
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => onToggle(row)}
                        disabled={!!row.deleted_at || submitting}
                      >
                        {row.is_active ? 'Deactivate' : 'Activate'}
                      </SecondaryButton>
                      <DangerButton
                        type="button"
                        onClick={() => setDeleteTarget(row)}
                        disabled={!!row.deleted_at}
                      >
                        Delete
                      </DangerButton>
                    </ActionsCell>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>

      {formModal && (
        <Modal
          onClick={closeForm}
          role="presentation"
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {formMode === 'create' ? 'Add advisory member' : 'Edit advisory member'}
            </ModalTitle>
            {fieldErrors._form && <FieldError style={{ display: 'block', marginBottom: 12 }}>{fieldErrors._form}</FieldError>}
            <form onSubmit={onSubmitForm}>
              <FormGrid>
                <FormGroup>
                  <Label htmlFor="f-name">Name *</Label>
                  <Input
                    id="f-name"
                    value={formValues.name}
                    onChange={(e) => setFormValues((v) => ({ ...v, name: e.target.value }))}
                    autoComplete="name"
                  />
                  {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="f-des">Designation *</Label>
                  <Input
                    id="f-des"
                    value={formValues.designation}
                    onChange={(e) => setFormValues((v) => ({ ...v, designation: e.target.value }))}
                  />
                  {fieldErrors.designation && <FieldError>{fieldErrors.designation}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="f-co">Company *</Label>
                  <Input
                    id="f-co"
                    value={formValues.company}
                    onChange={(e) => setFormValues((v) => ({ ...v, company: e.target.value }))}
                  />
                  {fieldErrors.company && <FieldError>{fieldErrors.company}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="f-qual">Qualification *</Label>
                  <Input
                    id="f-qual"
                    value={formValues.qualification}
                    onChange={(e) => setFormValues((v) => ({ ...v, qualification: e.target.value }))}
                  />
                  {fieldErrors.qualification && <FieldError>{fieldErrors.qualification}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="f-cat">Category</Label>
                  <FilterSelect
                    id="f-cat"
                    value={formValues.category_id}
                    onChange={(e) => setFormValues((v) => ({ ...v, category_id: e.target.value }))}
                    disabled={categoriesLoading}
                  >
                    <option value="">No category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </FilterSelect>
                  {fieldErrors.category_id && <FieldError>{fieldErrors.category_id}</FieldError>}
                </FormGroup>
                <FullSpan>
                  <Label htmlFor="f-photo">Profile photo *</Label>
                  <PhotoSection>
                    <PhotoPreviewWrap aria-hidden>
                      {previewObjectUrl ||
                      String(formValues.profile_image_url || '').trim() ? (
                        <img
                          src={
                            previewObjectUrl ||
                            String(formValues.profile_image_url || '').trim()
                          }
                          alt=""
                        />
                      ) : (
                        initials(formValues.name)
                      )}
                    </PhotoPreviewWrap>
                    <PhotoControls>
                      <FileInput
                        id="f-photo"
                        type="file"
                        accept={ADVISORY_IMAGE_ACCEPT}
                        onChange={onProfileFileChange}
                      />
                      <Hint>
                        JPEG or PNG only, max 5 MB. The file is uploaded to storage when you
                        save; the advisory record stores the returned public URL only.
                      </Hint>
                      {imageFile && (
                        <GhostButton type="button" onClick={clearImageSelection}>
                          Clear new selection
                        </GhostButton>
                      )}
                      {fieldErrors.profile_image && (
                        <FieldError>{fieldErrors.profile_image}</FieldError>
                      )}
                    </PhotoControls>
                  </PhotoSection>
                </FullSpan>
                <FormGroup>
                  <Label htmlFor="f-order">Display order *</Label>
                  <Input
                    id="f-order"
                    type="number"
                    min={0}
                    step={1}
                    value={formValues.display_order === '' ? '' : formValues.display_order}
                    onChange={(e) =>
                      setFormValues((v) => ({
                        ...v,
                        display_order:
                          e.target.value === '' ? '' : parseInt(e.target.value, 10),
                      }))
                    }
                  />
                  {fieldErrors.display_order && (
                    <FieldError>{fieldErrors.display_order}</FieldError>
                  )}
                </FormGroup>
                <FullSpan>
                  <Label htmlFor="f-bio">Bio *</Label>
                  <TextArea
                    id="f-bio"
                    value={formValues.bio}
                    onChange={(e) => setFormValues((v) => ({ ...v, bio: e.target.value }))}
                  />
                  {fieldErrors.bio && <FieldError>{fieldErrors.bio}</FieldError>}
                </FullSpan>
                <FullSpan>
                  <Label htmlFor="f-li">LinkedIn URL (optional)</Label>
                  <Input
                    id="f-li"
                    value={formValues.linkedin_url}
                    onChange={(e) => setFormValues((v) => ({ ...v, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/in/…"
                  />
                  {fieldErrors.linkedin_url && (
                    <FieldError>{fieldErrors.linkedin_url}</FieldError>
                  )}
                </FullSpan>
              </FormGrid>
              <ButtonRow>
                <GhostButton type="button" onClick={closeForm}>
                  Cancel
                </GhostButton>
                <PrimaryModalButton type="submit" disabled={submitting}>
                  {submitting ? 'Saving…' : formMode === 'create' ? 'Create' : 'Save changes'}
                </PrimaryModalButton>
              </ButtonRow>
            </form>
          </ModalCard>
        </Modal>
      )}

      {deleteTarget && (
        <Modal onClick={() => !submitting && setDeleteTarget(null)} role="presentation">
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Delete advisory member</ModalTitle>
            <p style={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.5 }}>
              This will soft-delete{' '}
              <strong>{deleteTarget.name}</strong>. You can confirm to proceed or cancel.
            </p>
            <ButtonRow>
              <GhostButton type="button" disabled={submitting} onClick={() => setDeleteTarget(null)}>
                Cancel
              </GhostButton>
              <PrimaryModalButton
                type="button"
                style={{ background: '#c53030' }}
                disabled={submitting}
                onClick={confirmDelete}
              >
                {submitting ? 'Deleting…' : 'Delete'}
              </PrimaryModalButton>
            </ButtonRow>
          </ModalCard>
        </Modal>
      )}
    </Container>
  );
}

export default AdvisoryManagement;
